import * as React from "react"
import {
  StructuredLogLevel,
  BuildStatus,
  StructuredLog,
  BuildRunnerType,
  SiteFunctionEntry,
} from "@modules/graphql/types"
import { navigate } from "gatsby"
import { useLocation } from "@reach/router"
import {
  StickyObserver,
  NumberBadge,
  NumberBadgeTone,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useTabsContext,
} from "gatsby-interface"
import { excludeActivitiesList } from "@modules/build/logs/structured/constants"
import { useStructuredLogs } from "../hooks/useStructuredLogs"
import { StructuredLogsTimeline } from "./StructuredLogsTimeline"
import { useTracker } from "@modules/analytics"
import Loading from "@modules/ui/components/Loading"
import { BuildRawLogsList } from "../../raw/components/BuildRawLogsList"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { BuildFunctionsList } from "@modules/functions/components/BuildFunctionsList"
import { useFunctionsForSiteQuery } from "@modules/functions/queries.generated"
import { useFlags } from "@modules/featureFlags"
import { StructuredLogsFilter } from "./StructuredLogsFilter"
import { build as text } from "@modules/locales/default"
import { BuildSsrLogs } from "@modules/build/logs/ssr/components/BuildSsrLogs"

const LogsTypeHashes = [
  `all`,
  `errors`,
  `warnings`,
  `rawLogs`,
  `ssrLogs`,
  `functions`,
]

function getHashes(flag: boolean) {
  return !flag
    ? LogsTypeHashes
    : LogsTypeHashes.filter(item => ![`errors`, `warnings`].includes(item))
}

function getTabIndexFromHash(hash = ``, hashes: string[]) {
  const idx = hashes.indexOf(hash)
  return idx !== -1 ? idx : 0
}

function getHashFromTabIndex(index = 0, hashes: string[]) {
  return hashes[index] ?? 0
}

export type StructuredLogsTabsProps = {
  buildId: string
  siteId: string
  organizationId: string
  runnerType: BuildRunnerType
  buildStatus: BuildStatus
}

export function StructuredLogsTabs({
  buildId,
  siteId,
  organizationId,
  buildStatus,
  runnerType,
}: StructuredLogsTabsProps) {
  const { flags } = useFlags()
  const [logsFilterValue, setLogsFitlerValue] = React.useState<
    StructuredLogLevel
  >(StructuredLogLevel.All)
  const { trackButtonClicked } = useTracker()
  const location = useLocation()
  const locationHash = location?.hash.replace(`#`, ``)
  const hashes = getHashes(flags.gatsbyv4)
  const tabIndex = getTabIndexFromHash(locationHash, hashes)

  const [
    structuredLogsForBuild,
    { loading, error, refetch },
  ] = useStructuredLogs({
    buildId,
    siteId,
    logLevel: StructuredLogLevel.All,
  })

  const { data: functionsData } = useFunctionsForSiteQuery({
    variables: {
      siteId,
      buildId,
    },
  })
  const functionsForSite = functionsData?.functionsForSite

  const logTabs = getStructuredLogsTabs(structuredLogsForBuild)
  const showRawLogs = ![BuildStatus.Canceled, BuildStatus.Queued].includes(
    buildStatus
  )

  React.useEffect(() => {
    if (flags.gatsbyv4 && locationHash === `errors`) {
      setLogsFitlerValue(StructuredLogLevel.Error)
    }

    if (flags.gatsbyv4 && locationHash === `warnings`) {
      setLogsFitlerValue(StructuredLogLevel.Warning)
    }
  }, [])

  const handleOnChange = (activeTab: number) => {
    trackRawLogsTab(activeTab)

    navigate(`${location.pathname}#${getHashFromTabIndex(activeTab, hashes)}`)
  }

  const trackRawLogsTab = (activeTab: number) => {
    if (hashes[activeTab] !== `rawLogs`) {
      return
    }

    trackButtonClicked(`View raw log`, {
      organizationId,
      uiSource: `Build/Preview list`,
      buildId,
    })
  }

  const successfullyBuilt = [
    BuildStatus.Success,
    BuildStatus.PublishCanceled,
    BuildStatus.PublishError,
  ].includes(buildStatus)

  const showFunctions = successfullyBuilt

  const showSsrLogs = flags.gatsbyv4 && successfullyBuilt

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading) {
    return <Loading message={text.messages.loadingLogs} bufferSize="padded" />
  }

  return (
    <React.Fragment>
      <Tabs onChange={handleOnChange} index={tabIndex}>
        <StickyObserver
          lipShadowPosition="bottom"
          css={theme => ({ backgroundColor: theme.colors.white })}
        >
          <TabList>
            {flags.gatsbyv4 ? (
              <Tab>{text.headers.logs}</Tab>
            ) : (
              logTabs.map(({ level, label, count }, tabIndex) => {
                return (
                  <Tab key={level}>
                    {label}
                    {typeof count === "number" && (
                      <StructuredLogsTabBadge
                        count={count}
                        level={level}
                        tabIndex={tabIndex}
                      />
                    )}
                  </Tab>
                )
              })
            )}

            {showRawLogs && <Tab>{text.headers.rawLogs}</Tab>}

            {showSsrLogs && <Tab>{text.headers.ssrLogs}</Tab>}

            {showFunctions && <Tab>{text.headers.functions}</Tab>}
          </TabList>
        </StickyObserver>
        <TabPanels css={theme => ({ paddingTop: theme.space[7] })}>
          {flags.gatsbyv4 ? (
            <TabPanel>
              <StructuredLogsFilter
                filterValue={logsFilterValue}
                setFilterValue={setLogsFitlerValue}
              />
              <StructuredLogsTimeline
                structuredLogs={filterStructuredLogsByLevel(
                  structuredLogsForBuild,
                  logsFilterValue
                )}
                refetch={refetch}
                logLevel={logsFilterValue}
                buildStatus={buildStatus}
                runnerType={runnerType}
              />
            </TabPanel>
          ) : (
            logTabs.map(({ level, logs }) => {
              return (
                <TabPanel key={level}>
                  <StructuredLogsTimeline
                    structuredLogs={logs}
                    refetch={refetch}
                    logLevel={level}
                    buildStatus={buildStatus}
                    runnerType={runnerType}
                  />
                </TabPanel>
              )
            })
          )}

          {showRawLogs && (
            <TabPanel>
              <BuildRawLogsList buildId={buildId} siteId={siteId} />
            </TabPanel>
          )}

          {showSsrLogs && (
            <TabPanel>
              <BuildSsrLogs buildId={buildId} siteId={siteId} />
            </TabPanel>
          )}

          {showFunctions && (
            <TabPanel>
              <BuildFunctionsList
                buildId={buildId}
                siteId={siteId}
                organizationId={organizationId}
                functionsForSite={functionsForSite as SiteFunctionEntry[]}
              />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </React.Fragment>
  )
}

const ignoredActivities = excludeActivitiesList.reduce<Record<string, boolean>>(
  (merged, name) => {
    merged[name] = true
    return merged
  },
  {}
)

const isErrorLog = ({ level }: StructuredLog) =>
  level === StructuredLogLevel.Error
const isWarningLog = ({ level }: StructuredLog) =>
  level === StructuredLogLevel.Warning

function filterStructuredLogsByLevel(
  logs: StructuredLog[],
  level: StructuredLogLevel = StructuredLogLevel.All
) {
  const validLogs = logs.filter(log =>
    log.activity?.name && ignoredActivities[log.activity?.name] ? false : true
  )

  if (level !== StructuredLogLevel.All) {
    return validLogs.filter((log: StructuredLog) => log.level === level)
  }

  return validLogs
}

function getStructuredLogsTabs(structuredLogsForBuild: StructuredLog[]) {
  const allLogs = structuredLogsForBuild.filter(log => {
    if (log.activity?.name && ignoredActivities[log.activity?.name]) {
      return false
    }

    return true
  })

  const errorLogs = allLogs.filter(isErrorLog)
  const warningLogs = allLogs.filter(isWarningLog)

  return [
    {
      level: StructuredLogLevel.All,
      label: `All Logs`,
      logs: allLogs,
      count: null,
    },
    {
      level: StructuredLogLevel.Error,
      label: `Errors`,
      logs: errorLogs,
      count: errorLogs.length,
    },
    {
      level: StructuredLogLevel.Warning,
      label: `Warnings`,
      logs: warningLogs,
      count: warningLogs.length,
    },
  ]
}

type StructuredLogsTabBadgeProps = {
  count: number
  level: string
  tabIndex: number
}

function StructuredLogsTabBadge({
  count,
  level,
  tabIndex,
}: StructuredLogsTabBadgeProps) {
  const { selectedIndex } = useTabsContext()

  let tone: NumberBadgeTone = `NEUTRAL`
  // We only want to apply non-neutral tone to inactive tabs
  if (tabIndex !== selectedIndex && count > 0) {
    tone = level === StructuredLogLevel.Error ? `DANGER` : `WARNING`
  }

  return (
    <NumberBadge tone={tone} css={theme => ({ marginLeft: theme.space[3] })}>
      {count}
    </NumberBadge>
  )
}
